import {
  agentCreateSessionUrl,
  agentDeleteSessionUrl,
  agentRunSseUrl,
  BOOKSY_ASSISTANT_APP_NAME,
} from '../../utils/agent-api-url'
import { AGENT_ERROR_CODES } from '../../consts/agent-errors'
import type { AgentEvent, AgentSession } from '../../types/agent'
import { formatAgentErrorResponse, createAgentError } from '../../utils/errors'
import type {
  AgentCredentialHeaders,
  BusinessSetupAgentSessionState,
} from '../types/session'
import { MESSAGE_ROLES } from '../../consts/chat-message'

export const AGENT_STREAM_TIMEOUT_MS = 90_000

function agentCountry (state: BusinessSetupAgentSessionState): string {
  return state.booksy_app_country ?? 'us'
}

async function parseJsonResponse<T> (response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text()
    throw new Error(formatAgentErrorResponse(response.status, body))
  }

  const body = await response.text()
  try {
    return JSON.parse(body) as T
  } catch {
    throw new Error(formatAgentErrorResponse(response.status, body))
  }
}

export async function createAgentSession ({
  userId,
  sessionId,
  state,
  credentialHeaders,
}: {
  userId: string | number
  sessionId?: string
  state: BusinessSetupAgentSessionState
  credentialHeaders: AgentCredentialHeaders
}): Promise<AgentSession> {
  const response = await fetch(
    agentCreateSessionUrl(userId, agentCountry(state)),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...credentialHeaders,
      },
      body: JSON.stringify({
        session_id: sessionId,
        state,
      }),
    }
  )

  return parseJsonResponse<AgentSession>(response)
}

export async function deleteAgentSession ({
  userId,
  sessionId,
  credentialHeaders,
  country,
}: {
  userId: string | number
  sessionId: string
  credentialHeaders: AgentCredentialHeaders
  country: string
}): Promise<void> {
  const response = await fetch(
    agentDeleteSessionUrl(userId, sessionId, country),
    {
      method: 'DELETE',
      headers: credentialHeaders,
    }
  )

  if (!response.ok) {
    const body = await response.text()
    throw new Error(formatAgentErrorResponse(response.status, body))
  }
}

export function terminateAgentSession ({
  userId,
  sessionId,
  credentialHeaders,
  country,
}: {
  userId: string | number
  sessionId: string
  credentialHeaders: AgentCredentialHeaders
  country: string
}): void {
  void deleteAgentSession({ userId, sessionId, credentialHeaders, country }).catch(() => {
    // Best-effort cleanup — ignore network or already-deleted sessions.
  })
}

export async function* streamAgentRun ({
  userId,
  sessionId,
  message,
  stateDelta,
  credentialHeaders,
}: {
  userId: string | number
  sessionId: string
  message: string
  stateDelta: BusinessSetupAgentSessionState
  credentialHeaders: AgentCredentialHeaders
}): AsyncGenerator<AgentEvent> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), AGENT_STREAM_TIMEOUT_MS)

  try {
    const response = await fetch(agentRunSseUrl(agentCountry(stateDelta)), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...credentialHeaders,
      },
      signal: controller.signal,
      body: JSON.stringify({
        app_name: BOOKSY_ASSISTANT_APP_NAME,
        user_id: String(userId),
        session_id: sessionId,
        streaming: true,
        state_delta: stateDelta,
        new_message: {
          role: MESSAGE_ROLES.USER,
          parts: [{ text: message }],
        },
      }),
    })

    if (!response.ok) {
      const body = await response.text()
      throw new Error(formatAgentErrorResponse(response.status, body))
    }

    if (!response.body) {
      throw createAgentError(AGENT_ERROR_CODES.EMPTY_STREAM)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    const cancelReader = (): void => {
      void reader.cancel()
    }
    controller.signal.addEventListener('abort', cancelReader)

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data:')) {
            continue
          }

          const payload = line.slice(5).trim()
          if (!payload) {
            continue
          }

          let event: AgentEvent & { error?: string }
          try {
            event = JSON.parse(payload) as AgentEvent & { error?: string }
          } catch {
            throw createAgentError(AGENT_ERROR_CODES.INVALID_RESPONSE)
          }
          if (event.error) {
            throw new Error(event.error)
          }

          yield event
        }
      }
    } finally {
      controller.signal.removeEventListener('abort', cancelReader)
    }
  } catch (error) {
    if (controller.signal.aborted) {
      throw createAgentError(AGENT_ERROR_CODES.STREAM_TIMEOUT)
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}
